import { useEffect, useState } from 'react'
import type { Actor, Project } from '../lib/types'
import { api } from '../lib/api'
import { errorText, useToast } from './Toaster'
import { Modal, Spinner, Switch } from '../ds'

export function ProfileSettingsModal({
  actor,
  onClose,
  onActorChange
}: {
  actor: Actor
  onClose: () => void
  onActorChange: (actor: Actor) => void
}) {
  const { notify } = useToast()
  const [emailNotifications, setEmailNotifications] = useState(actor.emailNotifications)
  const [projects, setProjects] = useState<Project[] | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [masterBusy, setMasterBusy] = useState(false)

  useEffect(() => {
    api
      .listProjects()
      .then((res) => setProjects(res.projects))
      .catch(() => setProjects([]))
  }, [])

  async function toggleMaster(next: boolean) {
    setMasterBusy(true)
    setEmailNotifications(next)
    try {
      const res = await api.updateProfile({ emailNotifications: next })
      onActorChange(res.actor)
    } catch (err) {
      setEmailNotifications(!next)
      notify(errorText(err), 'error')
    } finally {
      setMasterBusy(false)
    }
  }

  async function toggleProject(project: Project, next: boolean) {
    if (!project.id) return
    const projectId = project.id
    setBusyId(projectId)
    setProjects((list) => list?.map((p) => (p.id === projectId ? { ...p, emailNotifications: next } : p)) ?? null)
    try {
      await api.updateProjectNotifications(projectId, next)
    } catch (err) {
      setProjects((list) => list?.map((p) => (p.id === projectId ? { ...p, emailNotifications: !next } : p)) ?? null)
      notify(errorText(err), 'error')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <Modal title="Настройки" onClose={onClose}>
      <div className="stack">
        <div className="settings-row">
          <div>
            <div className="settings-row-title">Уведомления на email</div>
            <div className="settings-row-sub">
              Дайджест о новых постах автопоиска. Если выключить — письма не приходят ни по одному проекту.
            </div>
          </div>
          <Switch
            checked={emailNotifications}
            disabled={masterBusy}
            onCheckedChange={toggleMaster}
            aria-label="Уведомления на email"
          />
        </div>

        <div className={`settings-sub ${emailNotifications ? '' : 'is-disabled'}`}>
          <div className="settings-sub-head">По проектам</div>
          {projects === null ? (
            <div className="settings-sub-loading">
              <Spinner size={18} />
            </div>
          ) : projects.length === 0 ? (
            <div className="muted">Проектов пока нет.</div>
          ) : (
            projects.map((project) => (
              <div key={project.id} className="settings-row">
                <div className="settings-row-title">{project.name}</div>
                <Switch
                  checked={emailNotifications && project.emailNotifications}
                  disabled={!emailNotifications || busyId === project.id}
                  onCheckedChange={(next) => toggleProject(project, next)}
                  aria-label={`Уведомления о проекте «${project.name}»`}
                />
              </div>
            ))
          )}
        </div>
      </div>
    </Modal>
  )
}
